<script lang="ts">
    interface Props { name: string; mastery: number; description?: string; }
    let { name, mastery, description }: Props = $props();

    const REEL_DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
    const CELL_H = 1.5; // rem — must match .dc height in CSS

    function toDigits(val: number): [number, number, number] {
        const n = Math.max(0, Math.min(999, Math.round(val)));
        return [Math.floor(n / 100), Math.floor(n / 10) % 10, n % 10];
    }

    let displayVal = $state(mastery);
    let shuffling  = $state(false);
    let glimmer    = $state(false);

    let mounted = false;
    $effect(() => {
        const target = mastery;
        if (!mounted) { mounted = true; return; }

        glimmer = false;
        requestAnimationFrame(() => { glimmer = true; });

        shuffling = true;
        let step = 0;
        const id = setInterval(() => {
            step++;
            if (step < 6) {
                displayVal = Math.floor(Math.random() * 100);
            } else {
                clearInterval(id);
                shuffling = false;
                displayVal = target;
            }
        }, 75);

        return () => { clearInterval(id); };
    });

    let digits = $derived(toDigits(displayVal));
</script>

<div class="faculty-pill" class:shuffling class:glimmer title={description}>
    <span class="pill-name">{name}</span>
    <div class="odometer">
        {#each digits as digit, i}
            <div class="dw">
                <div class="dr"
                    style="transform: translateY(-{digit * CELL_H}rem); --delay: {i * 70}ms">
                    {#each REEL_DIGITS as n}
                        <div class="dc">{n}</div>
                    {/each}
                </div>
            </div>
        {/each}
    </div>
</div>

<style>
    /* ── Brass steampunk pill ─────────────────────────────────────────── */

    .faculty-pill {
        position: relative;
        flex: 0 0 auto;
        margin: 0 10px;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
        padding: 0.25rem 0.55rem;
        overflow: hidden;

        background:
            radial-gradient(circle, #f0d468 0%, #b08828 45%, #5c3e08 100%)
                no-repeat 2px 2px / 5px 5px,
            radial-gradient(circle, #f0d468 0%, #b08828 45%, #5c3e08 100%)
                no-repeat calc(100% - 2px) 2px / 5px 5px,
            radial-gradient(circle, #f0d468 0%, #b08828 45%, #5c3e08 100%)
                no-repeat 2px calc(100% - 2px) / 5px 5px,
            radial-gradient(circle, #f0d468 0%, #b08828 45%, #5c3e08 100%)
                no-repeat calc(100% - 2px) calc(100% - 2px) / 5px 5px,
            linear-gradient(160deg,
                #2e1e04 0%,
                #7a5610 18%,
                #c8960c 38%,
                #e8b820 50%,
                #c89010 62%,
                #7a5610 82%,
                #2e1e04 100%);

        border: 1px solid #c8a020;
        box-shadow:
            inset 0 1px 0 rgba(255, 230, 130, 0.35),
            inset 0 -2px 5px rgba(0, 0, 0, 0.65),
            0 1px 4px rgba(0, 0, 0, 0.5),
            0 0 0 1px rgba(60, 42, 6, 0.6);
    }

    /* Shimmer sweep — only fires when .glimmer is set */
    .faculty-pill::before {
        content: '';
        position: absolute;
        top: 0;
        left: -80%;
        width: 55%;
        height: 100%;
        background: linear-gradient(
            105deg,
            transparent 0%,
            rgba(255, 248, 200, 0.05) 30%,
            rgba(255, 255, 230, 0.22) 50%,
            rgba(255, 248, 200, 0.05) 70%,
            transparent 100%
        );
        pointer-events: none;
        z-index: 10;
        opacity: 0;
    }

    .faculty-pill.glimmer::before {
        animation: pill-shimmer 0.75s ease-out 1 forwards;
    }

    @keyframes pill-shimmer {
        0%   { left: -80%; opacity: 0; }
        8%   { opacity: 1; }
        100% { left: 140%; opacity: 0.6; }
    }

    /* ── Pill name label ──────────────────────────────────────────────── */

    .pill-name {
        font-size: 1.1rem;
        font-family: 'Courier New', Courier, monospace;
        font-weight: bold;
        letter-spacing: 0.08em;
        color: #2c1a06;
        text-transform: uppercase;
        text-shadow:
            0 1px 0 rgba(255, 220, 100, 0.4),
            0 -1px 0 rgba(0, 0, 0, 0.4);
        line-height: 1;
        align-self: center;
        position: relative;
        z-index: 5;
        padding-left: 1.1rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex: 1 1 0;
        min-width: 0;
    }

    /* ── Nixie-tube odometer ──────────────────────────────────────────── */

    .odometer {
        display: flex;
        gap: 1px;
        background: #0a0604;
        border: 1px solid #5a4208;
        box-shadow:
            inset 0 2px 6px rgba(0, 0, 0, 0.85),
            inset 0 0 0 1px rgba(0, 0, 0, 0.5);
        flex-shrink: 0;
        position: relative;
        z-index: 5;
        padding: 1px;
    }

    .dw {
        width: 1rem;
        height: 1.5rem; /* = CELL_H rem */
        overflow: hidden;
        position: relative;
        background: #060402;
    }

    .dw::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(
            to bottom,
            rgba(6, 4, 2, 0.85) 0%,
            transparent 28%,
            transparent 72%,
            rgba(6, 4, 2, 0.85) 100%
        );
        pointer-events: none;
        z-index: 2;
    }

    /* Normal transition; disabled during shuffle so digits jump instantly */
    .dr {
        transition: transform 0.75s cubic-bezier(0.12, 0, 0.2, 1) var(--delay, 0ms);
    }

    .shuffling .dr {
        transition: none;
    }

    .dc {
        height: 1.5rem; /* = CELL_H rem */
        line-height: 1.5rem;
        width: 1rem;
        text-align: center;
        font-size: 1rem;
        font-family: 'Courier New', Courier, monospace;
        color: #ff8820;
        text-shadow:
            0 0 4px rgba(255, 140, 30, 0.9),
            0 0 10px rgba(255, 100, 10, 0.5);
        position: relative;
        z-index: 1;
    }
</style>
